import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetDataSourceJsonDataDocument,
  type GetDataSourceJsonDataQuery,
  type GetDataSourceJsonDataQueryVariables,
  UpdateDataSourceJsonDataDocument,
  type UpdateDataSourceJsonDataMutation,
  type UpdateDataSourceJsonDataMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

export type JsonDataSourcePayload = { schemas: string[][]; rows: Record<string, unknown>[] };

// Client-side mirror of the backend's reserved-column protection: every "_"-prefixed
// system column plus the attribution columns is read-only in the editor. The backend
// re-enforces this; the client set only drives the UI.
const RESERVED_COLUMN_PREFIX = "_";
const PROTECTED_COLUMNS: ReadonlySet<string> = new Set(["reference", "page"]);
export const isProtectedColumn = (name: string): boolean =>
  name.startsWith(RESERVED_COLUMN_PREFIX) || PROTECTED_COLUMNS.has(name);

/**
 * Loads and saves the S3-backed JSON payload of a JSON data source for the
 * in-browser table editor. `save` requires the `s3Path` the payload was
 * loaded with, so the backend can reject stale saves (someone else edited
 * the source in between).
 */
export const useDataSourceJsonEditor = (dataSourceId: string) => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [saving, setSaving] = useState(false);
  const [getJsonData, { loading }] = useLazyQuery<GetDataSourceJsonDataQuery, GetDataSourceJsonDataQueryVariables>(
    GetDataSourceJsonDataDocument,
    { fetchPolicy: "no-cache" },
  );
  const [updateJsonData] = useMutation<UpdateDataSourceJsonDataMutation, UpdateDataSourceJsonDataMutationVariables>(
    UpdateDataSourceJsonDataDocument,
  );

  const load = useCallback(async () => {
    try {
      if (!tenantId) {
        throw new Error("Tenant is not resolved");
      }

      const { data } = await getJsonData({ variables: { input: { tenantId, dataSourceId } } });
      const result = data?.cnutGetDataSourceJsonData;
      if (result?.__typename === "GetDataSourceJsonDataOutput") {
        return { jsonData: result.jsonData as JsonDataSourcePayload, s3Path: result.s3Path };
      }
      toast.error(t("feature.dataImportProjects.detail.dataSource.jsonEdit.alert.loadFailed"));
      return null;
    } catch (e) {
      toast.error(translateError(t, e));
      return null;
    }
  }, [getJsonData, tenantId, dataSourceId, t]);

  const save = useCallback(
    async (jsonData: JsonDataSourcePayload, expectedS3Path: string) => {
      setSaving(true);
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const { data } = await updateJsonData({
          variables: { input: { tenantId, dataSourceId, jsonData, expectedS3Path } },
        });
        const result = data?.cnutUpdateDataSourceJsonData;
        if (result?.__typename === "DataSourceJsonDataUpdateOutput") {
          toast.success(t("feature.dataImportProjects.detail.dataSource.jsonEdit.alert.saved"));
          return true;
        }
        const message = result?.__typename === "ValidationError" ? result.fields[0]?.message : undefined;
        toast.error(message ?? t("feature.dataImportProjects.detail.dataSource.jsonEdit.alert.saveFailed"));
        return false;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [updateJsonData, tenantId, dataSourceId, t],
  );

  return { load, save, loading, saving };
};
